import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, Users, DollarSign, Shield, Trash2, Pencil, Tags, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planBadge = {
  grandfathered: { label: 'Legacy', cls: 'bg-amber-100 text-amber-700' },
  trial: { label: 'Trial', cls: 'bg-blue-100 text-blue-700' },
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' },
  past_due: { label: 'Past Due', cls: 'bg-red-100 text-red-700' },
  suspended: { label: 'Suspended', cls: 'bg-red-100 text-red-700' },
};

const TIER_STYLE = {
  bronze: 'bg-orange-50 border-orange-200 text-orange-700',
  silver: 'bg-slate-100 border-slate-300 text-slate-700',
  gold: 'bg-amber-50 border-amber-300 text-amber-700',
};

function PricingTiersCard() {
  const [tiers, setTiers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pricing-tiers`).then((res) => setTiers(res.data)).catch(() => toast.error('Failed to load pricing tiers'));
  }, []);

  const setField = (key, field, value) => {
    setTiers((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const handleSave = async () => {
    setConfirmSave(false);
    setSaving(true);
    try {
      const payload = {};
      for (const key of ['bronze', 'silver', 'gold']) {
        payload[key] = {
          annual_fee: parseFloat(tiers[key].annual_fee),
          per_user_monthly: parseFloat(tiers[key].per_user_monthly),
          min_users: parseInt(tiers[key].min_users, 10),
          max_users: parseInt(tiers[key].max_users, 10),
        };
        if (Object.values(payload[key]).some((v) => Number.isNaN(v))) {
          toast.error(`Invalid values for ${key} tier`);
          setSaving(false);
          return;
        }
      }
      const res = await axios.put(`${API}/admin/pricing-tiers`, payload);
      const orgCounts = res.data.organizations_updated || {};
      const totalOrgs = Object.values(orgCounts).reduce((s, n) => s + n, 0);
      const subs = res.data.stripe_subscriptions || [];
      const subFails = subs.filter((s) => !s.updated);
      toast.success(`Pricing saved — ${totalOrgs} organization(s) updated${subs.length ? `, ${subs.length - subFails.length}/${subs.length} Stripe subscription(s) repriced` : ''}`);
      if (subFails.length) toast.warning(`${subFails.length} Stripe subscription(s) could not be repriced — check backend logs`);
      setTiers(res.data.tiers);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  if (!tiers) return null;

  const fields = [
    { field: 'annual_fee', label: 'Annual Fee ($)', step: '0.01' },
    { field: 'per_user_monthly', label: 'Per User / Month ($)', step: '0.01' },
    { field: 'min_users', label: 'Min Users', step: '1' },
    { field: 'max_users', label: 'Max Users', step: '1' },
  ];

  return (
    <Card className="bg-white border border-slate-200 shadow-sm" data-testid="admin-pricing-card">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tags size={16} className="text-slate-500" />
          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500">Pricing Tiers</h4>
        </div>
        <Button size="sm" onClick={() => setConfirmSave(true)} disabled={saving} data-testid="save-pricing-btn">
          <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Pricing'}
        </Button>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {['bronze', 'silver', 'gold'].map((key) => (
          <div key={key} className={`border rounded-lg p-4 ${TIER_STYLE[key]}`} data-testid={`pricing-tier-${key}`}>
            <p className="font-bold text-sm uppercase tracking-wide mb-3">{tiers[key].label || key}</p>
            <div className="space-y-2.5">
              {fields.map(({ field, label, step }) => (
                <div key={field}>
                  <label className="text-[11px] font-semibold text-slate-500 block mb-0.5">{label}</label>
                  <Input
                    type="number"
                    step={step}
                    min="0"
                    value={tiers[key][field]}
                    onChange={(e) => setField(key, field, e.target.value)}
                    className="h-8 bg-white"
                    data-testid={`pricing-${key}-${field}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="px-6 pb-4 -mt-2">
        <p className="text-xs text-slate-400">Changes apply to new signups AND all existing organizations on each tier, including active Stripe subscriptions (new per-seat price takes effect next billing cycle).</p>
      </div>

      <AlertDialog open={confirmSave} onOpenChange={setConfirmSave}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update platform pricing?</AlertDialogTitle>
            <AlertDialogDescription>
              This updates pricing for new signups and ALL existing organizations on each tier. Active Stripe subscriptions will be repriced starting from their next billing cycle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave} data-testid="confirm-save-pricing">Update Pricing</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [renameOrg, setRenameOrg] = useState(null);
  const [renameName, setRenameName] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setData(res.data);
    } catch {
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrg = async (org) => {
    try {
      await axios.delete(`${API}/admin/organizations/${org.id}`);
      toast.success(`"${org.name}" deleted`);
      fetchData();
    } catch { toast.error('Failed to delete'); }
    setDeleteConfirm(null);
  };

  const handleRenameOrg = async () => {
    if (!renameName.trim() || !renameOrg) return;
    try {
      await axios.patch(`${API}/admin/organizations/${renameOrg.id}`, { name: renameName.trim() });
      toast.success(`Renamed to "${renameName.trim()}"`);
      setRenameOrg(null);
      fetchData();
    } catch { toast.error('Failed to rename'); }
  };

  if (!user?.is_superadmin) {
    return (
      <DashboardLayout>
        <div className="text-center p-12"><p className="text-slate-600">Superadmin access required.</p></div>
      </DashboardLayout>
    );
  }

  if (loading || !data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-slate-600">Loading admin dashboard...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-dashboard-page" className="space-y-6">
        <div>
          <h3 className="text-2xl font-bold text-slate-900 mb-1">Platform Admin</h3>
          <p className="text-slate-500 text-sm">Overview of all registered companies and users</p>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="stat-total-orgs">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 p-2 rounded-md"><Building2 size={18} className="text-blue-600" /></div>
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Companies</p>
            </div>
            <p className="text-3xl font-black text-slate-900">{data.total_organizations}</p>
          </Card>
          <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="stat-total-users">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-purple-100 p-2 rounded-md"><Users size={18} className="text-purple-600" /></div>
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Total Users</p>
            </div>
            <p className="text-3xl font-black text-slate-900">{data.active_users} <span className="text-sm font-normal text-slate-400">/ {data.total_users} total</span></p>
          </Card>
          <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="stat-revenue">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-emerald-100 p-2 rounded-md"><DollarSign size={18} className="text-emerald-600" /></div>
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Total Revenue</p>
            </div>
            <p className="text-3xl font-black text-slate-900">${data.total_revenue.toFixed(2)}</p>
            <p className="text-xs text-slate-400 mt-1">Annual: ${data.annual_revenue.toFixed(2)} | Monthly: ${data.monthly_revenue.toFixed(2)}</p>
          </Card>
          <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="stat-plans">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-orange-100 p-2 rounded-md"><Shield size={18} className="text-orange-600" /></div>
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500">By Plan</p>
            </div>
            <div className="space-y-1">
              {Object.entries(data.plan_counts).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${planBadge[plan]?.cls || 'bg-slate-100 text-slate-600'}`}>
                    {planBadge[plan]?.label || plan}
                  </span>
                  <span className="text-sm font-bold text-slate-700">{count}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Pricing Tiers */}
        <PricingTiersCard />

        {/* Organizations Table */}
        <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500">All Companies ({data.organizations.length})</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-orgs-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Tier</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Users</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Pricing</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Annual Paid</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Monthly Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.organizations.map((org) => {
                  const badge = planBadge[org.plan_type] || { label: org.plan_type, cls: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={org.id} className="hover:bg-slate-50" data-testid={`admin-org-${org.id}`}>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{org.name}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 capitalize">{org.plan_tier_key}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 font-medium">
                        {org.user_count} / {org.max_users === 999 ? 'Unlimited' : org.max_users}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {org.pricing_tier?.annual_fee > 0
                          ? `$${org.pricing_tier.annual_fee}/yr + $${org.pricing_tier.per_user_monthly}/user`
                          : 'Free (legacy)'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {org.annual_paid_until ? new Date(org.annual_paid_until).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {org.monthly_paid_until ? new Date(org.monthly_paid_until).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                            onClick={() => { setRenameOrg(org); setRenameName(org.name); }}
                            data-testid={`rename-org-${org.id}`}
                          ><Pencil size={12} /></Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => setDeleteConfirm(org)}
                            data-testid={`delete-org-${org.id}`}
                          ><Trash2 size={12} /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deleteConfirm?.name}" and all its users ({deleteConfirm?.user_count})? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteOrg(deleteConfirm)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename Dialog */}
      <AlertDialog open={!!renameOrg} onOpenChange={(open) => { if (!open) setRenameOrg(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename Organization</AlertDialogTitle>
            <AlertDialogDescription>
              <Input value={renameName} onChange={(e) => setRenameName(e.target.value)} className="mt-2" data-testid="rename-input" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRenameOrg}>Rename</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
